import React, { useState } from 'react';
import {
  Button, Dropdown, FormController, FormLabel, MenuItem, TextField, Typography
} from '@one-thd/sui-atomic-components';
import { zipCodeRegex } from '@thd-olt-functional/utils';
import PropTypes from 'prop-types';
import {
  IconWithRoundBorder
} from '../IconWithRoundBorder/IconWithRoundBorder';
import constants from '../../constants/constants';
import { MobileAwareOverlay } from '../../../components/MobileAwareOverlay';
import './inputAddressModal.scss';

export const InputAddressModal = ({ handleClose, isMobile, handleContinue }) => {

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [zip5, setZip5] = useState('');

  const handleContinueClick = () => {
    handleContinue({
      firstName,
      lastName,
      addressLine1,
      addressLine2,
      state: selectedState.substring(0, 2),
      city,
      zip5,
      standardized: false,
    });
  };

  const validateZipCode = (fieldValue) => {
    return zipCodeRegex.test(fieldValue);
  };

  const isZipInvalid = zip5 && !validateZipCode(zip5);

  const isEnabled = firstName.length > 0
    && lastName.length > 0
    && addressLine1.length > 0
    && city.length > 0
    && selectedState.length > 0
    && validateZipCode(zip5);

  const handleZip5Change = (event) => {
    const val = event.target.value.replace(/\D/g, '').slice(0, 5);
    setZip5(val);
  };

  return (
    <MobileAwareOverlay
      modalClass="preferences-modal"
      onClose={handleClose}
      open
    >
      <div className="sui-grid sui-gap-4 preferences-modal__content">
        <div className="sui-flex sui-items-center sui-gap-4">
          {!isMobile && (
            <IconWithRoundBorder
              iconSrc="https://assets.homedepot-static.com/images/v1/mailbox.svg"
              iconWidth="27.7px"
            />
          )}
          <Typography variant="h4">Remove From Mailing List</Typography>
        </div>
        <Typography variant="body-base">
          To be removed from The Home Depot mailing list, please provide your mailing information below.
          Please make sure all required information is correctly entered. When finished, click &quot;Continue.&quot;
        </Typography>

        <div className="sui-grid sui-grid-cols-12 sui-gap-4">
          <div className="sui-col-span-12 md:sui-col-span-6">
            <TextField
              name="firstName"
              label="First Name"
              type="text"
              defaultValue={firstName}
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              fullWidth
            />
          </div>
          <div className="sui-col-span-12 md:sui-col-span-6">
            <TextField
              name="lastName"
              label="Last Name"
              type="text"
              defaultValue={lastName}
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              fullWidth
            />
          </div>
        </div>
        <div className="sui-grid sui-grid-cols-12 sui-gap-4">
          <div className="sui-col-span-12 2xl:sui-col-span-6">
            <TextField
              name="street"
              label="Street Name"
              type="text"
              defaultValue={addressLine1}
              value={addressLine1}
              onChange={(event) => setAddressLine1(event.target.value)}
              fullWidth
            />
          </div>
          <div className="sui-col-span-12 2xl:sui-col-span-6">
            <TextField
              name="apartment"
              label="Unit, Apartment Number, Building Number, etc."
              type="text"
              defaultValue={addressLine2}
              value={addressLine2}
              onChange={(event) => setAddressLine2(event.target.value)}
              fullWidth
            />
          </div>
        </div>
        <div className="sui-grid sui-grid-cols-12 sui-gap-4">
          <div className="sui-col-span-12 md:sui-col-span-6">
            <TextField
              name="city"
              label="City"
              type="text"
              defaultValue={city}
              value={city}
              onChange={(event) => setCity(event.target.value)}
              fullWidth
            />
          </div>
          <div className="sui-col-span-12 md:sui-col-span-6">
            <FormController fullWidth>
              <FormLabel>State</FormLabel>
              <Dropdown
                name="stateSelect"
                label="State"
                placeholder=""
                value={selectedState}
                onChange={(event) => setSelectedState(event.target.value)}
                fullWidth
              >
                {constants.STATES.map((st) => (
                  <MenuItem key={st.value} value={st.value}>{st.name}</MenuItem>
                ))}
              </Dropdown>
            </FormController>
          </div>
        </div>
        <div className="sui-grid sui-grid-cols-12 sui-gap-4">
          <div className="sui-col-span-12 md:sui-col-span-3">
            <TextField
              name="zipcode"
              label="Zip Code"
              type="text"
              maxLength="5"
              defaultValue={zip5}
              value={zip5}
              onChange={handleZip5Change}
              status={isZipInvalid ? 'error' : undefined}
              statusMessage={isZipInvalid ? 'Please enter a valid 5-digit ZIP Code.' : undefined}
              fullWidth
            />
          </div>
        </div>

        <hr className="hr-line" />
        <div className="sui-grid sui-grid-cols-12">
          <div className="sui-col-span-9" />
          <div className="sui-col-span-3">
            <Button
              data-testid="postalUnsubContinueButton"
              onClick={handleContinueClick}
              disabled={!isEnabled}
              fullWidth
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    </MobileAwareOverlay>
  );
};

InputAddressModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  handleContinue: PropTypes.func.isRequired,
};

InputAddressModal.defaultProps = {
  isMobile: false,
};
